import { supabase } from '../supabase';
import { SharingPermissionException } from '../utils/types';

/**
 * Get all sharing permission exceptions with exporter and species details
 * @returns Array of all sharing exceptions
 */
export const getAllSharingExceptions = async (): Promise<
  SharingPermissionException[]
> => {
  try {
    const { data, error } = await supabase
      .from('sharing_permissions_exceptions')
      .select(`
        *,
        exporter:exporters(id, name),
        species:species(id, name)
      `)
      .order('exporter_id', { ascending: true });

    if (error) throw new Error(error.message);
    return (data as SharingPermissionException[]) || [];
  } catch (error) {
    console.error('Error fetching sharing exceptions:', error);
    throw error;
  }
};

/**
 * Get all sharing exceptions for a specific exporter
 * @param exporterId - The ID of the exporter
 * @returns Array of exceptions for the exporter
 */
export const getExporterExceptions = async (
  exporterId: number
): Promise<SharingPermissionException[]> => {
  try {
    const { data, error } = await supabase
      .from('sharing_permissions_exceptions')
      .select('*')
      .eq('exporter_id', exporterId);

    if (error) throw new Error(error.message);
    return (data as SharingPermissionException[]) || [];
  } catch (error) {
    console.error('Error fetching exporter exceptions:', error);
    throw error;
  }
};

/**
 * Check if a specific exporter-species-season combination is restricted
 * @param exporterId - The ID of the exporter
 * @param speciesId - The ID of the species
 * @param season - Optional season (e.g., "24-25"). If provided, checks for both season-specific and global restrictions
 * @returns true if restricted (exception exists), false if allowed to share
 */
export const isExporterRestricted = async (
  exporterId: number,
  speciesId: number,
  season?: string
): Promise<boolean> => {
  try {
    let query = supabase
      .from('sharing_permissions_exceptions')
      .select('id')
      .eq('exporter_id', exporterId)
      .eq('species_id', speciesId);

    // Check for both season-specific and global restrictions
    if (season) {
      query = query.or(`season.eq.${season},season.is.null`);
    } else {
      query = query.is('season', null);
    }

    const { data, error } = await query;

    if (error) throw new Error(error.message);
    return data && data.length > 0;
  } catch (error) {
    console.error('Error checking if exporter is restricted:', error);
    // Fail open: allow access on error to avoid blocking users unnecessarily
    return false;
  }
};

/**
 * Create a new sharing permission exception
 * @param exception - The exception data (without id and created_at)
 * @returns The created exception
 */
export const createSharingException = async (
  exception: Omit<SharingPermissionException, 'id' | 'created_at'>
): Promise<SharingPermissionException> => {
  try {
    const { data, error } = await supabase
      .from('sharing_permissions_exceptions')
      .insert(exception)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data as SharingPermissionException;
  } catch (error) {
    console.error('Error creating sharing exception:', error);
    throw error;
  }
};

/**
 * Update an existing sharing permission exception
 * @param id - The ID of the exception to update
 * @param exception - The fields to update (typically only season)
 * @returns The updated exception
 */
export const updateSharingException = async (
  id: number,
  exception: Partial<SharingPermissionException>
): Promise<SharingPermissionException> => {
  try {
    const { data, error } = await supabase
      .from('sharing_permissions_exceptions')
      .update(exception)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data as SharingPermissionException;
  } catch (error) {
    console.error('Error updating sharing exception:', error);
    throw error;
  }
};

/**
 * Delete a sharing permission exception
 * @param id - The ID of the exception to delete
 */
export const deleteSharingException = async (id: number): Promise<void> => {
  try {
    const { error } = await supabase
      .from('sharing_permissions_exceptions')
      .delete()
      .eq('id', id);

    if (error) throw new Error(error.message);
  } catch (error) {
    console.error('Error deleting sharing exception:', error);
    throw error;
  }
};
